<script setup lang="ts">
import type { WishlistLink } from "@/models/wishlist"
import type { ICuratedSelectionRequest } from "@/types/wishlist.interface"
import { computed, ref, watch, watchEffect } from "vue"
import { getInvalidWishlistLinkUrls, useGetEventVault, useGetWishlist, useSaveWishlist } from "@/api"
import { FreemiumBadge, FreemiumUpgradeCard } from "@/components"
import { Alert, Button, FormSection, Input } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { WishlistMode } from "@/types/wishlist.interface"
import { isAmazonUrl } from "@/utils"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/wishlist", value => Number(value))

const { t } = useTranslation()
const toast = useToast()

const brand = import.meta.env.VITE_APP_NAME ?? "GUESTPIX"

const eventVaultStore = useEventVaultStore()
const { data: eventVault, isLoading: isEventVaultLoading } = useGetEventVault(eventVaultStore.eventVaultId, { include: ["zip", "website"] })

const { data: wishlist, isLoading: isWishlistLoading, refetch } = useGetWishlist(eventVaultId, { includeCurated: true })
const { mutate: saveWishlist, isPending: isSaving } = useSaveWishlist(eventVaultId)

const isLoading = computed(() => isWishlistLoading.value || isEventVaultLoading.value)

const MAX_LINKS = 5
const EMPTY_CUSTOM_LINKS = [""]

const wishlistMode = ref<WishlistMode>(WishlistMode.Off)
const customLinks = ref<string[]>(EMPTY_CUSTOM_LINKS)
const selectedCuratedLinks = ref<ICuratedSelectionRequest[]>([])
const invalidLinkUrls = ref<string[]>([])
const isFormInitialised = ref(false)

const noWishlistAccess = computed(() => eventVault.value?.eventVaultSettings?.wishlistMode === undefined || eventVault.value?.eventVaultSettings?.wishlistMode === null)

const isCustomModeLocked = computed(() =>
  !!wishlist.value?.availableModes?.length && !wishlist.value.availableModes.includes(WishlistMode.Custom),
)

function isOptionDisabled(mode: WishlistMode) {
  return mode === WishlistMode.Custom && isCustomModeLocked.value
}

const wishlistModeOptions = computed(() => [
  {
    value: WishlistMode.Off,
    title: t("wishlist_page.modes.off.title"),
    description: t("wishlist_page.modes.off.description"),
  },
  {
    value: WishlistMode.Curated,
    title: t("wishlist_page.modes.curated.title", { brand }),
    description: t("wishlist_page.modes.curated.description", { brand }),
  },
  {
    value: WishlistMode.Custom,
    title: t("wishlist_page.modes.custom.title"),
    description: t("wishlist_page.modes.custom.description"),
  },
])

const canAddCustomLink = computed(() => customLinks.value.length < MAX_LINKS)

const customLinksToSave = computed(() => {
  if (wishlistMode.value !== WishlistMode.Custom)
    return null

  return customLinks.value.map(link => link.trim()).filter(Boolean)
})

const hasAmazonCustomLink = computed(() =>
  customLinks.value.some(link => link.trim() && isAmazonUrl(link)),
)

const curatedLinksToSave = computed(() => {
  if (wishlistMode.value !== WishlistMode.Curated)
    return null

  return selectedCuratedLinks.value
})

const disableSave = computed(() => {
  if (noWishlistAccess.value || !wishlist.value)
    return true

  if (wishlistMode.value === WishlistMode.Custom && isCustomModeLocked.value)
    return true

  if (wishlistMode.value === WishlistMode.Custom)
    return !customLinksToSave.value?.length

  if (wishlistMode.value === WishlistMode.Curated)
    return !curatedLinksToSave.value?.length

  return false
})

function getLinkError(url: string) {
  if (!invalidLinkUrls.value.some(link => link.trim().toLowerCase() === url.trim().toLowerCase()))
    return undefined

  return t("wishlist_page.link_error")
}

function isCuratedLinkSelected(link: WishlistLink) {
  return selectedCuratedLinks.value.some(
    selected => selected.provider === link.provider && selected.key === link.key,
  )
}

function toCuratedSelection(link: WishlistLink) {
  if (!link.provider || !link.key)
    return null

  return { provider: link.provider, key: link.key }
}

function clearSaveErrors() {
  invalidLinkUrls.value = []
}

function resetFormFields() {
  customLinks.value = [...EMPTY_CUSTOM_LINKS]
  selectedCuratedLinks.value = []
}

function savedLinkUrls(links: WishlistLink[]) {
  return links.flatMap(link => link.url ? [link.url] : [])
}

function curatedSelectionsFromSavedUrls(savedUrls: string[], curatedItems: WishlistLink[]) {
  return savedUrls.flatMap((url) => {
    const match = curatedItems.find(item => item.url === url)
    const selection = match ? toCuratedSelection(match) : null
    return selection ? [selection] : []
  })
}

function initialiseFormFromWishlist() {
  const saved = wishlist.value
  if (!saved)
    return

  wishlistMode.value = saved.wishlistMode ?? WishlistMode.Off
  const savedUrls = savedLinkUrls(saved.links)

  switch (saved.wishlistMode) {
    case WishlistMode.Custom:
      customLinks.value = savedUrls.length ? savedUrls : [...EMPTY_CUSTOM_LINKS]
      selectedCuratedLinks.value = []
      break
    case WishlistMode.Curated:
      customLinks.value = [...EMPTY_CUSTOM_LINKS]
      selectedCuratedLinks.value = curatedSelectionsFromSavedUrls(savedUrls, saved.curatedItems)
      break
    default:
      resetFormFields()
  }

  isFormInitialised.value = true
}

function addCustomLink() {
  if (!canAddCustomLink.value)
    return

  customLinks.value.push("")
}

function toggleCuratedLink(link: WishlistLink, checked: boolean) {
  const selection = toCuratedSelection(link)

  if (!selection)
    return

  if (checked) {
    if (!isCuratedLinkSelected(selection) && selectedCuratedLinks.value.length < MAX_LINKS)
      selectedCuratedLinks.value = [...selectedCuratedLinks.value, selection]
    return
  }

  selectedCuratedLinks.value = selectedCuratedLinks.value.filter(
    selected => !(selected.provider === selection.provider && selected.key === selection.key),
  )
}

function handleSave() {
  clearSaveErrors()

  if (disableSave.value)
    return

  saveWishlist({
    wishlistMode: wishlistMode.value,
    links: customLinksToSave.value,
    curatedLinks: curatedLinksToSave.value,
  }, {
    onSuccess: async () => {
      isFormInitialised.value = false
      await refetch()
      initialiseFormFromWishlist()
      toast.success({ description: t("wishlist_page.save_success_message") })
    },
    onError: (error) => {
      const invalidUrls = getInvalidWishlistLinkUrls(error)

      if (invalidUrls?.length) {
        invalidLinkUrls.value = invalidUrls
        return
      }

      toast.error({ description: t("wishlist_page.save_error_message") })
    },
  })
}

watchEffect(() => {
  if (wishlist.value && !isFormInitialised.value)
    initialiseFormFromWishlist()
})

watch([customLinks, selectedCuratedLinks, wishlistMode], () => {
  if (invalidLinkUrls.value.length)
    clearSaveErrors()
}, { deep: true })
</script>

<template>
  <SidebarHostEventLayout>
    <div class="flex flex-col gap-6 md:gap-10">
      <div class="flex flex-col gap-2 max-w-4xl">
        <h1 class="font-heading font-bold text-xl">
          {{ t("wishlist_page.title") }}
        </h1>
        <p class="text-label text-sm md:text-base font-body">
          {{ t("wishlist_page.description") }}
        </p>
      </div>

      <div v-if="isLoading" data-test="wishlist-loading" class="flex flex-col gap-4">
        <div class="gpx-skeleton h-48 w-full rounded-xl" />
        <div class="gpx-skeleton h-12 w-36 rounded-lg" />
      </div>

      <form v-else class="flex flex-col gap-6" @submit.prevent="handleSave">
        <Alert
          v-if="noWishlistAccess"
          variant="info"
          prefix-icon="CircleInfo"
          :closable="false"
          data-test="wishlist-no-access"
        >
          {{ t("wishlist_page.no_access_message") }}
        </Alert>

        <FormSection
          :title="t('wishlist_page.section_title')"
          icon="Gift"
          variant="divider"
          :border-top="false"
        >
          <fieldset
            class="m-0 min-w-0 border border-gray-200 rounded-xl overflow-hidden bg-white p-0 cursor-not-allowed"
            :disabled="noWishlistAccess"
            :class="{ 'opacity-60': noWishlistAccess }"
          >
            <div
              v-for="(option, index) in wishlistModeOptions"
              :key="option.value"
              class="block bg-white"
              :class="index > 0 ? 'border-t border-gray-200' : ''"
            >
              <div class="flex gap-3 p-4">
                <input
                  :id="`wishlist-mode-${option.value}`"
                  v-model="wishlistMode"
                  type="radio"
                  name="wishlistMode"
                  :value="option.value"
                  :data-test="`wishlist-mode-${option.value}`"
                  :disabled="isOptionDisabled(option.value)"
                  class="mt-0.5 size-4 shrink-0 accent-primary"
                >
                <label
                  :for="`wishlist-mode-${option.value}`"
                  class="min-w-0 flex-1"
                  :class="isOptionDisabled(option.value) ? 'cursor-not-allowed' : 'cursor-pointer'"
                >
                  <div class="flex flex-col gap-1" :class="{ 'opacity-60': isOptionDisabled(option.value) }">
                    <div class="flex items-center gap-2">
                      <p class="font-heading font-bold text-base text-label">
                        {{ option.title }}
                      </p>
                      <FreemiumBadge v-if="option.value === WishlistMode.Custom && isCustomModeLocked" variant="icon" />
                    </div>

                    <p class="text-sm text-gray-600">
                      {{ option.description }}
                    </p>
                  </div>

                  <div
                    v-if="option.value === WishlistMode.Custom && isCustomModeLocked && eventVault?.upgradeUrl"
                    class="mt-3 max-w-xs"
                    data-test="wishlist-custom-locked"
                  >
                    <FreemiumUpgradeCard variant="mini" :upgrade-url="eventVault?.upgradeUrl" />
                  </div>
                </label>
              </div>

              <div
                v-if="option.value === WishlistMode.Curated && wishlistMode === WishlistMode.Curated"
                class="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4"
              >
                <div v-if="!wishlist?.curatedItems.length" data-test="wishlist-no-gift-guides" class="text-sm text-gray-500">
                  {{ t("wishlist_page.no_gift_guides") }}
                </div>

                <template v-else>
                  <label
                    v-for="curatedOption in wishlist?.curatedItems"
                    :key="`${curatedOption.provider}-${curatedOption.key}`"
                    data-test="wishlist-curated-option"
                    class="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white p-3"
                    :class="isCuratedLinkSelected(curatedOption) ? 'border-primary' : ''"
                  >
                    <input
                      type="checkbox"
                      class="mt-0.5 size-4 shrink-0 accent-primary"
                      :checked="isCuratedLinkSelected(curatedOption)"
                      :disabled="!isCuratedLinkSelected(curatedOption) && selectedCuratedLinks.length >= MAX_LINKS"
                      @change="toggleCuratedLink(curatedOption, ($event.target as HTMLInputElement).checked)"
                    >
                    <img
                      v-if="curatedOption.providerAssetUrl"
                      :src="curatedOption.providerAssetUrl"
                      :alt="curatedOption.label ?? ''"
                      class="size-8 shrink-0 object-contain"
                    >
                    <span class="min-w-0 text-sm">
                      <span class="block font-semibold text-label break-all">{{ curatedOption.label }}</span>
                      <span class="mt-1 block text-xs text-gray-500 break-all">{{ curatedOption.url }}</span>
                      <span v-if="curatedOption.url && getLinkError(curatedOption.url)" class="mt-1 block text-xs text-danger">
                        {{ getLinkError(curatedOption.url) }}
                      </span>
                    </span>
                  </label>

                  <p class="text-xs text-gray-500" data-test="wishlist-amazon-associate-disclosure">
                    {{ t("wishlist_page.amazon_associate_disclosure", { brand }) }}
                  </p>
                </template>
              </div>

              <div
                v-if="option.value === WishlistMode.Custom && wishlistMode === WishlistMode.Custom && !isCustomModeLocked"
                data-test="wishlist-custom-links"
                class="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4"
              >
                <div
                  v-for="(_, linkIndex) in customLinks"
                  :key="linkIndex"
                >
                  <Input
                    v-model="customLinks[linkIndex]"
                    :name="`wishlistLink-${linkIndex}`"
                    :label="t('wishlist_page.custom_link_label', { number: linkIndex + 1 })"
                    type="url"
                    :placeholder="t('wishlist_page.custom_link_placeholder')"
                    clearable
                    :error="getLinkError(customLinks[linkIndex])"
                    @clear="customLinks[linkIndex] = ''"
                  />
                </div>

                <Button
                  type="button"
                  variant="tertiary"
                  prefix-icon="CirclePlus"
                  class="w-fit"
                  data-test="wishlist-add-link"
                  :disabled="!canAddCustomLink"
                  @click="addCustomLink"
                >
                  {{ t("wishlist_page.add_more") }}
                </Button>

                <p
                  v-if="hasAmazonCustomLink"
                  class="text-xs text-gray-500"
                  data-test="wishlist-amazon-associate-disclosure"
                >
                  {{ t("wishlist_page.amazon_associate_disclosure", { brand }) }}
                </p>
              </div>
            </div>
          </fieldset>
        </FormSection>

        <div class="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            class="w-full sm:w-auto"
            data-test="wishlist-save"
            :disabled="isSaving || disableSave"
          >
            {{ isSaving ? t("wishlist_page.saving") : t("wishlist_page.save_changes") }}
          </Button>
        </div>
      </form>
    </div>
  </SidebarHostEventLayout>
</template>
